/**
 * Tracks which connector imports are currently RUNNING, keyed by run id, so the
 * connectors settings page can show the source's Import button as a loader while
 * a background run is in flight.
 *
 * Why a store (not the tray store): the run's lifecycle spans from the moment
 * `useStartConnectorImport` returns a `runId` (before any page event arrives —
 * discovery can take seconds on a large notebook) until the backend emits
 * `connector-import:completed`. The upload tray, by contrast, only knows about
 * per-page rows once `item-queued` events start flowing, so it can't cover the
 * discovery window or map a run back to its toolkit. This slice holds exactly
 * `runId → toolkitKey` for the "is this connector importing?" question.
 *
 *  - `startRun`  — called on start-mutation success (`{ runId }` + toolkit).
 *  - `endRun`    — called on `connector-import:completed` for that run.
 *
 * A run older than {@link MAX_RUN_AGE_MS} is treated as inactive by
 * `useIsToolkitImporting` — a backstop so a dropped `completed` event can't pin
 * the button in a spinning state forever.
 */

import { create } from 'zustand';

/** Max time a run is considered "in progress" before the loader self-clears. */
export const MAX_RUN_AGE_MS = 15 * 60 * 1000; // 15 minutes — far above any real import

interface ActiveRun {
  readonly toolkitKey: string;
  /** `Date.now()` when the run started — used for the staleness backstop. */
  readonly startedAt: number;
}

interface ConnectorImportRunsState {
  /** Active runs keyed by runId. */
  readonly runs: Readonly<Record<string, ActiveRun>>;
  /** Mark a run started for a toolkit (prunes stale entries as it goes). */
  readonly startRun: (runId: string, toolkitKey: string) => void;
  /** Mark a run finished (any terminal status). No-op if unknown. */
  readonly endRun: (runId: string) => void;
}

/** Drop entries older than the backstop window. */
function pruneStale(runs: Record<string, ActiveRun>, now: number): Record<string, ActiveRun> {
  const fresh: Record<string, ActiveRun> = {};
  for (const [id, run] of Object.entries(runs)) {
    if (now - run.startedAt < MAX_RUN_AGE_MS) fresh[id] = run;
  }
  return fresh;
}

export const useConnectorImportRunsStore = create<ConnectorImportRunsState>((set) => ({
  runs: {},
  startRun: (runId, toolkitKey) =>
    set((state) => {
      const now = Date.now();
      return { runs: { ...pruneStale(state.runs, now), [runId]: { toolkitKey, startedAt: now } } };
    }),
  endRun: (runId) =>
    set((state) => {
      if (!(runId in state.runs)) return state;
      const { [runId]: _removed, ...rest } = state.runs;
      return { runs: rest };
    }),
}));

/**
 * Whether ANY non-stale import run is currently in progress for `toolkitKey`.
 * Drives the Import button's loading state on the connectors settings page.
 */
export function useIsToolkitImporting(toolkitKey: string): boolean {
  return useConnectorImportRunsStore((state) => {
    const now = Date.now();
    return Object.values(state.runs).some(
      (run) => run.toolkitKey === toolkitKey && now - run.startedAt < MAX_RUN_AGE_MS,
    );
  });
}
